import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';
import { PageLoader } from '@/components/ui';
import { ProtectedRoute, PublicRoute } from '@/components/auth';
import { GlobalErrorBoundary, PageErrorBoundary } from '@/components/error';
import { AppShellLayout } from '@/components/layout';

// Lazy load pages
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const AuthCallbackPage = lazy(() => import('@/pages/AuthCallbackPage'));
const ClientsPage = lazy(() => import('@/pages/ClientsPage/ClientsPage'));
const ClientDetailPage = lazy(
  () => import('@/pages/ClientDetailPage/ClientDetailPage')
);
const ContractActionsPage = lazy(
  () => import('@/pages/ContractActionsPage/ContractActionsPage')
);
const ProductsPage = lazy(() => import('@/pages/ProductsPage/ProductsPage'));
const AssessmentsPage = lazy(
  () => import('@/pages/AssessmentsPage/AssessmentsPage')
);
const TicketsPage = lazy(() => import('@/pages/TicketsPage/TicketsPage'));
const CreditsPage = lazy(() => import('@/pages/CreditsPage/CreditsPage'));
const AdvanceDetailPage = lazy(
  () => import('@/pages/AdvanceDetailPage/AdvanceDetailPage')
);
const AlertsPage = lazy(() => import('@/pages/AlertsPage/AlertsPage'));
const HistoryPage = lazy(() => import('@/pages/HistoryPage/HistoryPage'));
const BankAccountSearchPage = lazy(
  () => import('@/pages/BankAccountSearchPage/BankAccountSearchPage')
);
const TasksPage = lazy(() => import('@/pages/TasksPage/TasksPage'));
const PaymentImportsPage = lazy(
  () => import('@/pages/PaymentImportsPage/PaymentImportsPage')
);
const PaymentImportsConfigsPage = lazy(
  () => import('@/pages/PaymentImportsConfigsPage/PaymentImportsConfigsPage')
);
const PaymentImportsHistoryPage = lazy(
  () => import('@/pages/PaymentImportsHistoryPage/PaymentImportsHistoryPage')
);
const ReportsPage = lazy(() => import('@/pages/ReportsPage/ReportsPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage/AdminPage'));
const ReferAFriendPage = lazy(
  () => import('@/pages/PromotionsPage/ReferAFriendPage')
);
const ReferralCampaignFormPage = lazy(
  () => import('@/pages/PromotionsPage/ReferralCampaignFormPage')
);
const ReferralCampaignDetailPage = lazy(
  () => import('@/pages/PromotionsPage/ReferralCampaignDetailPage')
);
const NotFoundPage = lazy(() => import('@/pages/ErrorPages/NotFoundPage'));
const ErrorPage = lazy(() => import('@/pages/ErrorPages/ErrorPage'));

// Wrapper component for routes with error boundaries
function RouteWrapper({ children }: { children: React.ReactNode }) {
  return (
    <PageErrorBoundary>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </PageErrorBoundary>
  );
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <PublicRoute>
        <RouteWrapper>
          <LoginPage />
        </RouteWrapper>
      </PublicRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/auth/callback',
    element: (
      <RouteWrapper>
        <AuthCallbackPage />
      </RouteWrapper>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShellLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to="/clients" replace />,
      },
      {
        path: 'clients',
        element: (
          <RouteWrapper>
            <ClientsPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'clients/:clientId',
        element: (
          <RouteWrapper>
            <ClientDetailPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'contract-actions',
        element: (
          <RouteWrapper>
            <ContractActionsPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'products',
        element: (
          <RouteWrapper>
            <ProductsPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'assessments',
        element: (
          <RouteWrapper>
            <AssessmentsPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'tickets',
        element: (
          <RouteWrapper>
            <TicketsPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'credits',
        element: (
          <RouteWrapper>
            <CreditsPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'alerts',
        element: (
          <RouteWrapper>
            <AlertsPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'history',
        element: (
          <RouteWrapper>
            <HistoryPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'bank-account-search',
        element: (
          <RouteWrapper>
            <BankAccountSearchPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'payment-imports',
        element: (
          <RouteWrapper>
            <PaymentImportsPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'payment-imports/configs',
        element: (
          <RouteWrapper>
            <PaymentImportsConfigsPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'payment-imports/history',
        element: (
          <RouteWrapper>
            <PaymentImportsHistoryPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'clients/:customerId/advances/:advanceId',
        element: (
          <RouteWrapper>
            <AdvanceDetailPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'tasks',
        element: (
          <RouteWrapper>
            <TasksPage />
          </RouteWrapper>
        ),
      },
      {
        // No slug: ReportsPage redirects to the first workspace report once
        // the catalog has loaded (or explains why there is none).
        path: 'reports',
        element: (
          <RouteWrapper>
            <ReportsPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'reports/:reportSlug',
        element: (
          <RouteWrapper>
            <ReportsPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'promotions',
        element: <Navigate to="/promotions/refer-a-friend" replace />,
      },
      {
        path: 'promotions/refer-a-friend',
        element: (
          <RouteWrapper>
            <ReferAFriendPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'promotions/refer-a-friend/new',
        element: (
          <RouteWrapper>
            <ReferralCampaignFormPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'promotions/refer-a-friend/:campaignId',
        element: (
          <RouteWrapper>
            <ReferralCampaignDetailPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'promotions/refer-a-friend/:campaignId/edit',
        element: (
          <RouteWrapper>
            <ReferralCampaignFormPage />
          </RouteWrapper>
        ),
      },
      {
        path: 'admin',
        element: (
          <RouteWrapper>
            <AdminPage />
          </RouteWrapper>
        ),
      },
    ],
  },
  // Catch-all route for 404 errors
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);

export function AppRouter() {
  return (
    <GlobalErrorBoundary>
      <RouterProvider router={router} />
    </GlobalErrorBoundary>
  );
}
